import { Link } from "react-router-dom";
import { ShieldCheck, Sun, Users, MapPin } from "lucide-react";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { CallCta } from "../components/CallCta";
import { Testimonials } from "../components/Testimonials";
import { SunLogo } from "../components/SunLogo";

const VALUES = [
  { icon: Sun, title: "Vigilance", text: "Like the sun over the prairie, our watch never sets. We protect people and property around the clock." },
  { icon: Users, title: "Community", text: "We hire locally, train thoroughly, and invest in the Manitoba communities we serve — First Nations included." },
  { icon: ShieldCheck, title: "Professionalism", text: "Manitoba-licensed guards, supervised shifts, GPS-verified patrols, and documented reporting on every site." },
  { icon: MapPin, title: "Reach", text: "One security partner for the whole province — southern cities, rural towns, and northern communities alike." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About 1st Nations Security | Manitoba Security Company"
        description="Learn about 1st Nations Security — an Indigenous-owned security guard company providing licensed guards, patrols & response across Manitoba, 24/7."
        path="/about/"
        jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF9100]" data-testid="breadcrumb-home">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">About</span>
          </nav>
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <SunLogo size={56} />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5">Indigenous-Owned & Operated</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">
              About <span className="text-[#FF9100]">1st Nations Security</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              1st Nations Security is an Indigenous-owned security guard company serving all of Manitoba. Our name — and the sun in our logo — reflect who we are: a company rooted in First Nations values of protection, watchfulness, and care for community, delivering modern, professional security services.
            </p>
            <p className="text-gray-400 leading-relaxed mb-4">
              We protect businesses, construction sites, industrial facilities, events, healthcare settings, retail stores, and residential buildings from Winnipeg to Churchill. Every engagement is backed by 24/7 dispatch, Manitoba-licensed guards, and digital reporting that keeps you informed.
            </p>
            <p className="text-gray-400 leading-relaxed">
              When you work with us, you get more than a guard at a post — you get a security partner accountable to you and to the communities we both call home.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A0B0E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-10">What Guides Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-[#14161C] border border-white/8 rounded-2xl p-6" data-testid={`value-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100] mb-4">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/security-services" data-testid="about-services-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">Explore our security services →</Link>
            <Link to="/service-areas" data-testid="about-areas-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">See where we work →</Link>
            <a href="https://1stnation-security.com" data-testid="about-national-site-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">Our national site: 1stnation-security.com →</a>
          </div>
        </div>
      </section>

      <Testimonials />
      <CallCta heading="Work With Manitoba's Indigenous Security Company" sub="Call or email any time — our dispatch team answers 24/7." testid="about-cta" />
    </div>
  );
}
